import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { TreeStructure, UserPlus, ArrowsDownUp } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/lib/api';

function renderTree(nodes, depth) {
  if (!nodes || nodes.length === 0) return null;
  return nodes.map(function(aff, i) {
    var subs = aff.sub_affiliates || [];
    var tierColor = depth === 0 ? 'bg-blue-600/20 text-blue-400' : depth === 1 ? 'bg-emerald-600/20 text-emerald-400' : 'bg-amber-600/20 text-amber-400';
    return (
      <div key={aff.affiliate_id || i} style={{ marginLeft: depth * 24 + 'px' }}>
        <div className="flex items-center gap-3 py-2 border-b border-zinc-800/30" data-testid={'tree-node-' + (aff.affiliate_id || i)}>
          <div className={'w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ' + tierColor}>
            T{aff.tier || depth + 1}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-zinc-200 font-medium truncate">{aff.name}</p>
            <p className="text-[10px] text-zinc-500">{aff.affiliate_id} {aff.company ? '| ' + aff.company : ''}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-sm text-emerald-400 font-mono">${(aff.total_earnings || 0).toLocaleString()}</p>
            <p className="text-[10px] text-zinc-500">{aff.parent_commission_rate || 0}% comm</p>
          </div>
        </div>
        {subs.length > 0 && renderTree(subs, depth + 1)}
      </div>
    );
  });
}

export default function SubAffiliates() {
  const [network, setNetwork] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', commission_split: 10 });

  useEffect(function() {
    setLoading(true);
    Promise.all([api.get('/hierarchy/network'), api.get('/hierarchy/commission-log')])
      .then(function(results) { setNetwork(results[0].data); setCommissions(results[1].data); })
      .catch(console.error).finally(function() { setLoading(false); });
  }, []);

  var invite = async function(e) {
    e.preventDefault();
    try {
      await api.post('/hierarchy/invite', form);
      toast.success('Invitation sent');
      setDialogOpen(false);
      setForm({ email: '', name: '', commission_split: 10 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  var networkData = Array.isArray(network) ? network : (network?.sub_affiliates ? [Object.assign({}, network, { name: 'Your Network' })] : []);
  var totalNetworkRev = !Array.isArray(network) ? network?.total_network_revenue : 0;
  var totalCommission = !Array.isArray(network) ? network?.commission_earned : 0;

  return (
    <div className="space-y-6" data-testid="sub-affiliates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <TreeStructure size={32} className="text-blue-400" weight="duotone" />
            Sub-affiliate Network
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Manage your hierarchical affiliate network</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="invite-sub-btn" className="bg-blue-600 hover:bg-blue-700 text-white gap-2"><UserPlus size={16} /> Invite Sub-affiliate</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
            <DialogHeader><DialogTitle className="font-heading text-white">Invite Sub-affiliate</DialogTitle></DialogHeader>
            <form onSubmit={invite} className="space-y-4">
              <div><Label className="text-zinc-300">Name</Label><Input data-testid="invite-name" value={form.name} onChange={function(e) { setForm(function(p) { return Object.assign({}, p, { name: e.target.value }); }); }} required className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" /></div>
              <div><Label className="text-zinc-300">Email</Label><Input data-testid="invite-email" type="email" value={form.email} onChange={function(e) { setForm(function(p) { return Object.assign({}, p, { email: e.target.value }); }); }} required className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" /></div>
              <div><Label className="text-zinc-300">Commission Split (%)</Label><Input data-testid="invite-commission" type="number" min="1" max="50" value={form.commission_split} onChange={function(e) { setForm(function(p) { return Object.assign({}, p, { commission_split: Number(e.target.value) }); }); }} className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" /></div>
              <Button data-testid="submit-invite" type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">Send Invitation</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {!Array.isArray(network) && (
        <div className="grid sm:grid-cols-3 gap-4">
          <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Network Revenue</p>
            <p className="text-2xl font-bold font-heading text-emerald-400 mt-1">${(totalNetworkRev || 0).toLocaleString()}</p>
          </CardContent></Card>
          <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Commission Earned</p>
            <p className="text-2xl font-bold font-heading text-blue-400 mt-1">${(totalCommission || 0).toLocaleString()}</p>
          </CardContent></Card>
          <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Sub-affiliates</p>
            <p className="text-2xl font-bold font-heading text-amber-400 mt-1">{network?.sub_affiliates?.length || 0}</p>
          </CardContent></Card>
        </div>
      )}

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3"><CardTitle className="font-heading text-lg text-white flex items-center gap-2"><TreeStructure size={20} className="text-blue-400" /> Network Hierarchy</CardTitle></CardHeader>
        <CardContent>
          {networkData.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-8">No sub-affiliates in your network yet.</p>
          ) : (
            <div>{renderTree(networkData, 0)}</div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3"><CardTitle className="font-heading text-lg text-white flex items-center gap-2"><ArrowsDownUp size={20} className="text-emerald-400" /> Commission Waterfall Log</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">ID</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Parent</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Tier</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Rate</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {commissions.slice(0, 20).map(function(c, i) {
                return (
                  <TableRow key={i} className="border-zinc-800/50" data-testid={'commission-row-' + i}>
                    <TableCell className="text-zinc-500 font-mono text-xs">{c.log_id}</TableCell>
                    <TableCell className="text-zinc-200">{c.parent_name}</TableCell>
                    <TableCell><Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-[10px]">Tier {c.tier}</Badge></TableCell>
                    <TableCell className="text-zinc-300">{c.commission_rate}%</TableCell>
                    <TableCell className="text-emerald-400 font-mono">${c.commission_amount}</TableCell>
                  </TableRow>
                );
              })}
              {commissions.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-zinc-500 py-8">No commission data yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
